import { useEffect, useState } from 'react';
import { api, type RingingPhones } from '../api.ts';
import { APP_STORE_URL, PLAY_URL } from '../self-service.ts';

/**
 * The truth this website must never blur: reminders ring on a phone.
 *
 * Everything else — the routine, the medicines, who is told — can be set
 * up here, but a browser tab cannot wake somebody at 8 am with an alarm.
 * So wherever a person sets up their own reminders, this says where they
 * ring and how to get the app: Google Play for Android, the App Store for
 * iPhone.
 */
export function GetTheApp({ how, compact }: {
  /** How they signed in here — the app needs the same, or it is another account. */
  how?: string | null;
  compact?: boolean;
}) {
  return (
    <div className={`rounded-xl border border-(--line) ${compact ? 'p-3' : 'p-4'}`}>
      <p className="font-medium">Reminders ring on a phone</p>
      <p className="mt-1 text-sm text-(--muted)">
        This website sets things up and shows what was taken. The alarms themselves ring in the DrBell
        app on a phone, even with no internet.{' '}
        {how ? <>Install it and sign in with <strong className="text-(--fg)">{how}</strong>, the same way as here.</>
          : 'Install it and sign in the same way as here.'}
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <a
          href={PLAY_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex min-h-11 items-center rounded-lg bg-(--accent) px-4 text-sm font-medium text-white"
        >
          Get it on Google Play
        </a>
        <a
          href={APP_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex min-h-11 items-center rounded-lg border border-(--line) px-4 text-sm font-medium hover:border-(--accent)"
        >
          Download on the App Store
        </a>
      </div>
    </div>
  );
}

/**
 * On a record whose reminders ring on the reader's own phones — their own
 * record, or one they set up for somebody with no phone — say whether any
 * phone is actually ringing them. None yet: the app is the next step.
 */
export function RingingHere({ patientId, how }: { patientId: string; how?: string | null }) {
  const [phones, setPhones] = useState<(RingingPhones & { mayChoose?: boolean }) | null>(null);
  useEffect(() => {
    let live = true;
    api
      .get<RingingPhones & { mayChoose?: boolean }>(`/api/patients/${patientId}/phones`)
      .then((r) => live && setPhones(r))
      .catch(() => live && setPhones(null));
    return () => {
      live = false;
    };
  }, [patientId]);

  // Only about the reader's own phones, and only when none rings yet.
  if (!phones?.mayChoose || phones.phones.length > 0) return null;
  return (
    <div className="mb-6">
      <p role="status" className="mb-2 rounded-lg border border-(--brand) bg-(--brand-soft) px-3 py-2 text-sm text-(--fg)">
        No phone is ringing these reminders yet.
      </p>
      <GetTheApp how={how} compact />
    </div>
  );
}
